import { LazyLoader } from '@bp/ui-components';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { graphql } from 'client/gql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { Suspense, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { useQuery } from 'urql';
import { BpPage } from '../../components/BpPage/BpPage';
import {
  DesktopNavigationTabs,
  type DesktopNavigationTabsType,
} from '../../components/NavigationTabs/Desktop/DesktopNavigationTabs';
import { OrganizationConfigContext } from '../../context/OrganizationConfigContext';
import { useAuthClaims } from '../../hooks/useAuthClaims';

export function SettingsPage() {
  const { t } = useTranslation();
  const organization = useAuthClaims().pimAuthClaims.getOrganization();
  const { threemaLicense, planungEnabled, procuratEnabled } = useContext(OrganizationConfigContext);

  const permissions = usePermissionChecker();

  const tabs: DesktopNavigationTabsType[] = [
    {
      title: t('common.overview'),
      path: '/settings',
      pathMatchEnd: true,
    },
    ...(permissions?.isOrganizationAdmin(organization)
      ? [
          {
            title: t('absences.title', { count: 2 }),
            path: 'absences',
          },
        ]
      : []),
    ...(permissions?.canAcceptCollaboration(organization) || permissions?.canCreateCollaboration(organization)
      ? [
          {
            title: t('collaborations.title'),
            path: 'collaborations',
          },
        ]
      : []),
    ...(permissions?.canUpdateCollaborationGroups()
      ? [
          {
            title: t('collaborations.courses'),
            path: 'collaboration-courses',
          },
        ]
      : []),
    ...(permissions?.canUpdateCollaborationGroups()
      ? [
          {
            title: t('collaborations.groups'),
            path: 'collaboration-groups',
          },
        ]
      : []),
    ...(planungEnabled && permissions?.canAccessProcuratImport()
      ? [
          {
            title: t('planungImport.title'),
            path: 'planung',
          },
        ]
      : []),
    ...(procuratEnabled && permissions?.canAccessProcuratImport()
      ? [
          {
            title: t('procuratImport.title'),
            path: 'procurat',
          },
        ]
      : []),
    ...(threemaLicense && permissions?.isOrganizationAdmin(organization)
      ? [
          {
            title: t('threema.title'),
            path: 'threema-work',
          },
        ]
      : []),
  ];

  return (
    <BpPage
      title={t('settings.title')}
      isForbidden={!permissions?.canViewSettings(organization)}
      organization={organization}
    >
      <QueryClientProvider client={new QueryClient()}>
        <DesktopNavigationTabs tabs={tabs} className='mb-6' />
        <Suspense fallback={<LazyLoader embedded transparent forceHeight='45vh' />}>{<Outlet />}</Suspense>
      </QueryClientProvider>
    </BpPage>
  );
}
